import styled from '@emotion/styled'
import {
    Backdrop,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    tableCellClasses
} from '@mui/material'
import { BASE_COLOR } from '../../../shared/constants/color.constant'
import { useEffect, useState } from 'react'
import { User } from '../interfaces/utilisateur.interface'
import DeleteIcon from '@mui/icons-material/Delete'
import NewAdminModal from '../../../shared/components/Modale/new-admin-modal.component'
import userServices from '../services/utilisateur.service'
import { toast } from 'react-toastify'
import ModaleValidationComponent from '../../../shared/components/Modale/modale-validation.component'

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: BASE_COLOR.WHITE,
        border: 0
    },
    '&:last-child td, &:last-child th': {
        border: 0
    },
    [`& .${tableCellClasses.root}`]: {
        borderBottom: 'none'
    }
}))

const StyledTableHeader = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: BASE_COLOR.CONTAINER,
        // fontSize: 15,
        fontWeight: 'bold'
        // textTransform: 'uppercase'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 15
    }
}))

const UtilisateurAdminComponent: React.FC<any> = () => {
    const [loading, setLoading] = useState(false)
    const [adminUsers, setAdminUsers] = useState<User[]>([])
    const [openAddModal, setOpenAddModal] = useState(false)
    const [refresh, setRefresh] = useState(true)
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
    const [selectedUser, setSelectedUser] = useState<User | null>(null)

    const fetchAllAdmins = () => {
        setLoading(true)
        userServices
            .getAllAdmin()
            .then(res => setAdminUsers(res.data.data))
            .catch(console.log)
            .finally(() => {
                setLoading(false)
                setRefresh(false)
            })
    }

    const deleteOne = (id: User['_id']) => {
        if (adminUsers.length !== 1) {
            userServices
                .deleteAUser(id)
                .then(() => {
                    toast.success('Utilisateur supprimé avec succès')
                    handleRefresh()
                })
                .catch(console.log)
        } else toast.error('Il doit exister au moins un administrateur')
    }

    const handleRefresh = () => setRefresh(true)

    useEffect(() => {
        if (refresh) fetchAllAdmins()
    }, [refresh])

    const [order, setOrder] = useState<'asc' | 'desc'>('asc')
    const [orderBy, setOrderBy] = useState<string>('fullName')

    const handleSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        const newOrder = isAsc ? 'desc' : 'asc'
        setOrder(newOrder)
        setOrderBy(property)

        const sorted = [...adminUsers].sort((a, b) => (newOrder === 'asc' ? handleSortBy(a, b, property) : handleSortBy(b, a, property)))

        setAdminUsers(sorted)
    }

    const handleSortBy = (a: User, b: User, property: string) => {
        switch (property) {
            case 'fullName':
                return `${a['lastname']} ${a['firstname']}`.localeCompare(`${b['lastname']} ${b['firstname']}`)
            default:
                return (a[property] ?? '').localeCompare(b[property])
        }
    }

    return (
        <>
            <div className="m-4">
                <Button onClick={() => setOpenAddModal(true)} variant="contained">
                    Ajouter un nouvel utilisateur admin
                </Button>
            </div>
            <TableContainer component="div" sx={{ width: '150vh' }}>
                <Table
                    stickyHeader
                    sx={{
                        [`& .${tableCellClasses.root}`]: {
                            borderBottom: 'none'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <StyledTableHeader sortDirection={orderBy === 'fullName' ? order : false}>
                                <TableSortLabel
                                    active={true}
                                    direction={orderBy === 'fullName' ? order : 'asc'}
                                    onClick={() => handleSort('fullName')}
                                >
                                    Nom
                                </TableSortLabel>
                            </StyledTableHeader>
                            <StyledTableHeader sortDirection={orderBy === 'email' ? order : false}>
                                <TableSortLabel
                                    active={true}
                                    direction={orderBy === 'email' ? order : 'asc'}
                                    onClick={() => handleSort('email')}
                                >
                                    Email
                                </TableSortLabel>
                            </StyledTableHeader>
                            <StyledTableHeader>Actions</StyledTableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {adminUsers.map(admin => (
                            <StyledTableRow key={0}>
                                <TableCell>
                                    {admin.firstname} {admin.lastname}
                                </TableCell>
                                <TableCell>{admin.email}</TableCell>
                                <TableCell>
                                    <div
                                        className="cursor-pointer"
                                        onClick={() => {
                                            setSelectedUser(admin)
                                            setOpenDeleteConfirmation(true)
                                        }}
                                    >
                                        <DeleteIcon />
                                    </div>
                                </TableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <NewAdminModal open={openAddModal} setOpen={setOpenAddModal} refresh={handleRefresh} />
            <ModaleValidationComponent
                openModale={openDeleteConfirmation}
                setOpen={setOpenDeleteConfirmation}
                message={`Voulez-vous vraiment supprimer cet utilisateur ?`}
                handleValidate={() => {
                    if (selectedUser) deleteOne(selectedUser._id)
                }}
            />
            <Backdrop open={loading} sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </>
    )
}
export default UtilisateurAdminComponent
